import {EventEmitter} from 'events';
import debounce from 'lodash/debounce';
import values from 'lodash/values';
import {ILocalizationService} from '../LocalizationService';
import {ITsCanvasAnnotateService} from '../ts-canvas-annotate/ts-canvas-annotate.service';
import {IChatApi} from '../ts-chat-api/ChatApi';

interface Rectangle {
    startX: number;
    startY: number;
    minW: number;
    minH: number;
    w: number;
    h: number;
}

interface ActiveHandle {
    dragTL: boolean;
    dragTR: boolean;
    dragBR: boolean;
    dragBL: boolean;
    dragT: boolean;
    dragR: boolean;
    dragB: boolean;
    dragL: boolean;
}

interface HandlersPositions {
    tl: {x: number; y: number};
    t: {x: number; y: number};
    tr: {x: number; y: number};
    r: {x: number; y: number};
    br: {x: number; y: number};
    b: {x: number; y: number};
    bl: {x: number; y: number};
    l: {x: number; y: number};
}

interface CanvasSize {
    rect: DOMRect;
    scaleX: number;
    scaleY: number;
}

export interface ITsScanArea {
    readonly initialized: boolean;
    readonly destroyed: boolean;
    init(canvas: HTMLCanvasElement): void;
    destroy(): boolean;
    clear(): void;
    draw(): void;
    busy(): void;
    free(): void;
    on(
        event: 'areaSelected',
        listener: (data: {rect: {x: number; y: number; width: number; height: number}}) => void
    ): this;
    removeAllListeners(event?: string): this;
}

export class TsScanArea extends EventEmitter implements ITsScanArea {
    private canvas: HTMLCanvasElement | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private _captureButton: HTMLButtonElement | null = null;
    private requestAnimationFrameId?: number;

    private pending = false;
    private _initialized = false;
    private _destroyed = false;
    private i = 0;
    private d = 0;

    private rect: Rectangle = {
        startX: 0,
        startY: 0,
        minW: 20,
        minH: 20,
        w: 0,
        h: 0
    };

    private activeHandle: ActiveHandle = {
        dragTL: false,
        dragTR: false,
        dragBR: false,
        dragBL: false,
        dragT: false,
        dragR: false,
        dragB: false,
        dragL: false
    };

    private areaDraging = false;
    private readonly captureBtnRadius = 30;

    // Service references - injected dependencies
    private translate?: (key: string) => string;

    // Event listeners - bound methods
    private readonly mouseDownListener: (e: Event) => void;
    private readonly mouseMoveListener: (e: Event) => void;
    private readonly mouseUpListener: (e: Event) => void;
    private readonly windowResizeListener: () => void;
    private readonly rotateListener: () => void;
    private readonly captureClick: () => void;

    constructor(
        private translationService: any,
        private annotateService: any
    ) {
        super();

        // Bind methods
        this.captureClick = this.handleCaptureClick.bind(this);
        this.mouseDownListener = this.handleMouseDown.bind(this);
        this.mouseMoveListener = this.handleMouseMove.bind(this);
        this.mouseUpListener = this.handleMouseUp.bind(this);
        this.windowResizeListener = debounce(this.handleWindowResize.bind(this), 200);
        this.rotateListener = this.handleRotate.bind(this);
        this.translate = this.translationService.translate;

        this.prepareCaptureBtn();
    }

    get initialized(): boolean {
        return this._initialized;
    }

    get destroyed(): boolean {
        return this._destroyed;
    }

    get closeEnough(): number {
        const canvasSize = this.getCanvasSize();
        if (!canvasSize) return 10;
        const scale = Math.min(canvasSize.scaleX, canvasSize.scaleY);
        return 10 * scale;
    }

    get handlersPositions(): HandlersPositions {
        return {
            tl: {x: this.rect.startX, y: this.rect.startY},
            t: {x: this.rect.startX + this.rect.w / 2, y: this.rect.startY},
            tr: {x: this.rect.startX + this.rect.w, y: this.rect.startY},
            r: {x: this.rect.startX + this.rect.w, y: this.rect.startY + this.rect.h / 2},
            br: {x: this.rect.startX + this.rect.w, y: this.rect.startY + this.rect.h},
            b: {x: this.rect.startX + this.rect.w / 2, y: this.rect.startY + this.rect.h},
            bl: {x: this.rect.startX, y: this.rect.startY + this.rect.h},
            l: {x: this.rect.startX, y: this.rect.startY + this.rect.h / 2}
        };
    }

    emit(name: string, data: any): boolean {
        return super.emit(name, data);
    }

    init(canvas: HTMLCanvasElement): void {
        this.canvas = canvas;
        this.ctx = this.canvas.getContext('2d');
        this._initialized = true;
        this._destroyed = false;

        if (this.annotateService) {
            this.annotateService.readonly = true;
        }

        this.initCanvas();
        this.addEventListeners();
        this.insertCaptureBtn();
        this.clear();
        this.draw();
    }

    private initCanvas(): void {
        if (!this.canvas) return;

        const rectInitialSize = {
            w: this.canvas.width * 0.5,
            h: this.canvas.height * 0.6 - 30
        };

        const rectInitialPos = {
            startX: (this.canvas.width - rectInitialSize.w) / 2,
            startY: (this.canvas.height - rectInitialSize.h) / 2
        };

        this.rect = {
            startX: rectInitialPos.startX,
            startY: rectInitialPos.startY,
            minW: 20,
            minH: 20,
            w: rectInitialSize.w,
            h: rectInitialSize.h
        };

        this.resetActiveHandles();
        this.areaDraging = false;
    }

    private addEventListeners(): void {
        if (!this.canvas) return;

        this.canvas.addEventListener('touchstart', this.mouseDownListener);
        this.canvas.addEventListener('touchmove', this.mouseMoveListener);
        this.canvas.addEventListener('touchend', this.mouseUpListener);
        this.canvas.addEventListener('mousedown', this.mouseDownListener);
        this.canvas.addEventListener('mousemove', this.mouseMoveListener);
        this.canvas.addEventListener('mouseup', this.mouseUpListener);
        if (this.annotateService) {
            this.annotateService.on('rotated', this.rotateListener);
        }
        window.addEventListener('resize', this.windowResizeListener);
    }

    private removeEventListeners(): void {
        if (!this.canvas) return;

        this.canvas.removeEventListener('touchstart', this.mouseDownListener);
        this.canvas.removeEventListener('touchmove', this.mouseMoveListener);
        this.canvas.removeEventListener('touchend', this.mouseUpListener);
        this.canvas.removeEventListener('mousedown', this.mouseDownListener);
        this.canvas.removeEventListener('mousemove', this.mouseMoveListener);
        this.canvas.removeEventListener('mouseup', this.mouseUpListener);
        window.removeEventListener('resize', this.windowResizeListener);
        if (this.annotateService) {
            this.annotateService.removeListener('rotated', this.rotateListener);
        }
    }

    destroy(): boolean {
        if (!this.canvas) {
            return false;
        }

        this._initialized = false;
        this._destroyed = true;

        this.removeEventListeners();
        if (this.annotateService) {
            this.annotateService.readonly = false;
        }
        this.clear();
        this.removeCaptureBtn();

        return true;
    }

    clear(): void {
        if (!this.ctx || !this.canvas) return;
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    private handleCaptureClick(): void {
        const {startX: x, startY: y, w: width, h: height} = this.rect;

        this.emit('areaSelected', {
            rect: {x, y, width, height}
        });

        this.destroy();
    }

    private handleRotate(): void {
        this.initCanvas();
        this.clear();
        this.draw();
    }

    private handleWindowResize(): void {
        setTimeout(() => {
            this.clear();
            this.draw();
        }, 100);
    }

    private handleMouseDown(e: Event): boolean {
        const {x: mouseX, y: mouseY} = this.getMousePos(e as MouseEvent | TouchEvent);
        const pos = this.handlersPositions;

        if (this.checkCapture(mouseX, mouseY)) {
            return false;
        }

        this.resetActiveHandles();

        this.setActiveHandler(mouseX, mouseY, pos.tl.x, pos.tl.y, 'dragTL');
        this.setActiveHandler(mouseX, mouseY, pos.t.x, pos.t.y, 'dragT');
        this.setActiveHandler(mouseX, mouseY, pos.tr.x, pos.tr.y, 'dragTR');
        this.setActiveHandler(mouseX, mouseY, pos.r.x, pos.r.y, 'dragR');
        this.setActiveHandler(mouseX, mouseY, pos.br.x, pos.br.y, 'dragBR');
        this.setActiveHandler(mouseX, mouseY, pos.b.x, pos.b.y, 'dragB');
        this.setActiveHandler(mouseX, mouseY, pos.bl.x, pos.bl.y, 'dragBL');
        this.setActiveHandler(mouseX, mouseY, pos.l.x, pos.l.y, 'dragL');

        this.setAreaDragging(mouseX, mouseY);

        if (this.areaDraging && this.canvas) {
            this.canvas.style.cursor = 'move';
        }

        return true;
    }

    private handleMouseMove(e: Event): void {
        const {x: mouseX, y: mouseY} = this.getMousePos(e as MouseEvent | TouchEvent);

        this.updateCursor(mouseX, mouseY);
        this.updateRectangle(mouseX, mouseY);
        this.clear();
        this.draw();
    }

    private handleMouseUp(): void {
        this.resetActiveHandles();
        this.areaDraging = false;
    }

    private updateRectangle(mouseX: number, mouseY: number): void {
        const rect = this.rect;

        if (this.activeHandle.dragTL) {
            rect.w += rect.startX - mouseX;
            rect.h += rect.startY - mouseY;
            rect.startX = mouseX;
            rect.startY = mouseY;
        } else if (this.activeHandle.dragT) {
            rect.h += rect.startY - mouseY;
            rect.startY = mouseY;
        } else if (this.activeHandle.dragTR) {
            rect.w = Math.abs(rect.startX - mouseX);
            rect.h += rect.startY - mouseY;
            rect.startY = mouseY;
        } else if (this.activeHandle.dragR) {
            rect.w = Math.abs(rect.startX - mouseX);
        } else if (this.activeHandle.dragBR) {
            rect.w = Math.abs(rect.startX - mouseX);
            rect.h = Math.abs(rect.startY - mouseY);
        } else if (this.activeHandle.dragB) {
            rect.h = Math.abs(rect.startY - mouseY);
        } else if (this.activeHandle.dragBL) {
            rect.w += rect.startX - mouseX;
            rect.h = Math.abs(rect.startY - mouseY);
            rect.startX = mouseX;
        } else if (this.activeHandle.dragL) {
            rect.w += rect.startX - mouseX;
            rect.startX = mouseX;
        } else if (this.areaDraging) {
            rect.startX = mouseX - rect.w / 2;
            rect.startY = mouseY - rect.h / 2;
        }
    }

    busy(): void {
        this.drawSpinner();
    }

    free(): void {
        this.clear();
        this.draw();
        if (this.requestAnimationFrameId) {
            window.cancelAnimationFrame(this.requestAnimationFrameId);
        }
    }

    draw(): void {
        this.checkBoundaries();
        this.drawBackground();
        this.drawSelectionRectangle();
        this.drawHandles();
    }

    private drawBackground(): void {
        if (!this.ctx || !this.canvas) return;

        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.clearRect(this.rect.startX, this.rect.startY, this.rect.w, this.rect.h);
    }

    private drawSelectionRectangle(): void {
        if (!this.ctx) return;

        this.ctx.strokeStyle = 'black';
        this.ctx.setLineDash([6]);
        this.ctx.strokeRect(this.rect.startX, this.rect.startY, this.rect.w, this.rect.h);

        this.ctx.strokeStyle = 'white';
        this.ctx.setLineDash([6]);
        this.ctx.strokeRect(this.rect.startX - 1, this.rect.startY - 1, this.rect.w + 2, this.rect.h + 2);
    }

    private drawCircle(x: number, y: number, radius: number): void {
        if (!this.ctx) return;

        this.ctx.fillStyle = '#489de9';
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, 2 * Math.PI);
        this.ctx.fill();
    }

    private drawHandles(): void {
        const pos = this.handlersPositions;
        const radius = this.closeEnough;

        Object.values(pos).forEach(({x, y}) => {
            this.drawCircle(x, y, radius);
        });
    }

    private updateCursor(mouseX: number, mouseY: number): void {
        if (!this.canvas) return;

        const pos = this.handlersPositions;
        const cursors = [
            {
                condition: () => this.checkCloseEnough(mouseX, pos.tl.x) && this.checkCloseEnough(mouseY, pos.tl.y),
                cursor: 'nw-resize'
            },
            {
                condition: () => this.checkCloseEnough(mouseX, pos.t.x) && this.checkCloseEnough(mouseY, pos.t.y),
                cursor: 'n-resize'
            },
            {
                condition: () => this.checkCloseEnough(mouseX, pos.tr.x) && this.checkCloseEnough(mouseY, pos.tr.y),
                cursor: 'ne-resize'
            },
            {
                condition: () => this.checkCloseEnough(mouseX, pos.r.x) && this.checkCloseEnough(mouseY, pos.r.y),
                cursor: 'e-resize'
            },
            {
                condition: () => this.checkCloseEnough(mouseX, pos.br.x) && this.checkCloseEnough(mouseY, pos.br.y),
                cursor: 'se-resize'
            },
            {
                condition: () => this.checkCloseEnough(mouseX, pos.b.x) && this.checkCloseEnough(mouseY, pos.b.y),
                cursor: 's-resize'
            },
            {
                condition: () => this.checkCloseEnough(mouseX, pos.bl.x) && this.checkCloseEnough(mouseY, pos.bl.y),
                cursor: 'sw-resize'
            },
            {
                condition: () => this.checkCloseEnough(mouseX, pos.l.x) && this.checkCloseEnough(mouseY, pos.l.y),
                cursor: 'w-resize'
            },
            {condition: () => this.areaDraging, cursor: 'move'},
            {condition: () => this.checkCapture(mouseX, mouseY), cursor: 'pointer'}
        ];

        const cursor = cursors.find((c) => c.condition())?.cursor || 'default';
        this.canvas.style.cursor = cursor;
    }

    private drawSpinner(): void {
        if (!this.ctx) return;

        this.i += 0.025;

        this.ctx.save();
        this.ctx.translate(this.rect.startX + this.rect.w / 2 - 50, this.rect.startY + this.rect.h / 2 - 50);
        this.ctx.clearRect(0, 0, 100, 100);
        this.ctx.beginPath();

        let t = this.i - Math.floor(this.i);
        let t2 = this.i - Math.floor(this.i);

        if (Math.floor(this.i) % 2 === 1) {
            t = 0;
        } else {
            t2 = 1;
        }

        if (Math.floor(this.i) % 2 === 0 && Math.floor(this.i) > this.d * 2) {
            this.d++;
        }

        const y = Math.pow(t, 2) / (Math.pow(t, 2) + Math.pow(1 - t, 2));
        const y2 = Math.pow(t2, 2) / (Math.pow(t2, 2) + Math.pow(1 - t2, 2));

        this.ctx.arc(
            50,
            50,
            25,
            y * 5 + Math.floor(this.i) * 5 + this.d * 1.28,
            y2 * 5 + 0.15 + Math.floor(this.i) * 5 + this.d * 1.28
        );
        this.ctx.lineWidth = 8;
        this.ctx.setLineDash([]);
        this.ctx.strokeStyle = '#1e99fd';
        this.ctx.stroke();
        this.ctx.restore();

        this.requestAnimationFrameId = window.requestAnimationFrame(this.drawSpinner.bind(this));
    }

    private checkBoundaries(): void {
        if (!this.canvas) return;

        const rect = this.rect;

        rect.startX = Math.max(0, Math.min(rect.startX, this.canvas.width - rect.w));
        rect.startY = Math.max(0, Math.min(rect.startY, this.canvas.height - rect.h));
        rect.w = Math.max(rect.minW, rect.w);
        rect.h = Math.max(rect.minH, rect.h);
    }

    private resetActiveHandles(): void {
        this.activeHandle = {
            dragTL: false,
            dragTR: false,
            dragBR: false,
            dragBL: false,
            dragT: false,
            dragR: false,
            dragB: false,
            dragL: false
        };
    }

    private setActiveHandler(x: number, y: number, xR: number, yR: number, key: keyof ActiveHandle): void {
        const isClose = this.checkCloseEnough(x, xR) && this.checkCloseEnough(y, yR);
        this.activeHandle[key] = isClose;
    }

    private getMousePos(evt: MouseEvent | TouchEvent): {x: number; y: number} {
        if (!this.canvas) return {x: 0, y: 0};

        const clientX = 'clientX' in evt ? evt.clientX : evt.changedTouches[0].clientX;
        const clientY = 'clientY' in evt ? evt.clientY : evt.changedTouches[0].clientY;
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    }

    private checkCloseEnough(p1: number, p2: number, closeEnough = this.closeEnough): boolean {
        return Math.abs(p1 - p2) < closeEnough;
    }

    private checkCapture(x: number, y: number): boolean {
        if (!this.canvas) return false;

        const radius = this.captureBtnRadius;
        return (
            this.checkCloseEnough(x, this.canvas.width / 2, radius) &&
            this.checkCloseEnough(y, this.canvas.height - radius * 2 - 15, radius)
        );
    }

    private setAreaDragging(x: number, y: number): void {
        const rect = this.rect;
        const noBoundsMoving = !values(this.activeHandle).some((v) => !!v);

        this.areaDraging =
            noBoundsMoving &&
            ((x > rect.startX && x < rect.startX + rect.w) || (y > rect.startY && y < rect.startY + rect.h));
    }

    private prepareCaptureBtn(): void {
        const captureBtnText = document.createElement('span');
        captureBtnText.innerText = this.translate?.('MAIN.VIEW.MODE.CAPTURE') || 'Capture';

        this._captureButton = document.createElement('button');
        this._captureButton.setAttribute('id', 'scan-capture-btn');
        this._captureButton.appendChild(captureBtnText);
    }

    private insertCaptureBtn(): void {
        if (!this._captureButton || !this.canvas) return;

        this._captureButton.addEventListener('click', this.captureClick);
        this.canvas.insertAdjacentElement('afterend', this._captureButton);
    }

    private removeCaptureBtn(): void {
        if (!this._captureButton) return;

        this._captureButton.removeEventListener('click', this.captureClick);
        this._captureButton.remove();
    }

    private getCanvasSize(): CanvasSize | undefined {
        if (!this.canvas) return undefined;

        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        return {rect, scaleX, scaleY};
    }
}
